import { useState } from 'react';
import { readTraceId } from '../traceId';

function phoenixEndpoint(): string | null {
  const base = process.env.NEXT_PUBLIC_PHOENIX_API_URL;
  if (!base) return null;
  return `${base.replace(/\/$/, '')}/public/form-events`;
}

function getSessionId(formPublicId: string): string {
  const key = `lead-form-session:${formPublicId}`;
  let id: string | null = null;
  try {
    id = sessionStorage.getItem(key);
  } catch {
    // sessionStorage may throw in privacy modes — bail to ephemeral id.
  }
  if (!id) {
    id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      sessionStorage.setItem(key, id);
    } catch {
      /* ignore */
    }
  }
  return id;
}

export type FormAnalyticsEvent =
  | 'form_view'
  | 'form_started'
  | 'step_reached'
  | 'form_completed';

type TrackOptions = {
  step_uuid?: string;
  metadata?: Record<string, any>;
};

/**
 * Consumer-side analytics for lead forms (INS-667).
 *
 * Sends one event per call to Phoenix `POST /public/form-events`.
 * Fire-and-forget — network failures are silently ignored so analytics never
 * blocks the form UX. `keepalive: true` lets the browser flush in-flight
 * requests even after the tab unloads (e.g. immediate submit → close).
 *
 * SSR-safe: the lazy `useState` initializer runs on client only (guarded by
 * `typeof window`); FormView is a Client Component but Next.js still renders
 * it on the server for the initial HTML shell, and `sessionStorage` is
 * undefined there.
 */
export function useFormAnalytics(formPublicId: string | undefined) {
  const [sessionId] = useState<string | null>(() => {
    if (typeof window === 'undefined' || !formPublicId) return null;
    return getSessionId(formPublicId);
  });

  return {
    track: (event: FormAnalyticsEvent, options?: TrackOptions) => {
      if (!formPublicId || !sessionId) return;
      const endpoint = phoenixEndpoint();
      if (!endpoint) return; // Phoenix URL not configured — skip silently.
      const body = JSON.stringify({
        form_public_id: formPublicId,
        session_id: sessionId,
        event,
        step_uuid: options?.step_uuid ?? null,
        trace_id: readTraceId(),
        metadata: options?.metadata ?? null,
      });
      try {
        fetch(endpoint, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body,
        }).catch(() => {
          /* fire-and-forget */
        });
      } catch {
        /* fire-and-forget */
      }
    },
  };
}
